import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, Lightbulb, Copy, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { copyToClipboard } from "@/lib/clipboard";

export default function BancoIdeias() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = () => {
    const params = {};
    if (search) params.search = search;
    api.get("/ideas", { params }).then(r => setItems(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */}, []);

  const downloadFile = async (idea) => {
    if (!idea.has_file) return;
    setDownloading(idea.id);
    try {
      const { data } = await api.get(`/ideas/${idea.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = idea.file_name || `${idea.title}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success("Download iniciado!");
    } catch {
      toast.error("Não foi possível baixar o arquivo.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="banco-ideias-page">
      <h1 className="font-display font-semibold text-xl text-[hsl(var(--primary))]">Banco de PLs</h1>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Pesquisar projeto de lei…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          data-testid="ideas-search-input"
        />
      </div>
      {items.length === 0 ? (
        <EmptyState icon={Lightbulb} title="Nenhum projeto de lei encontrado" description="Use o campo de busca acima ou tente novamente em breve." />
      ) : items.map(i => (
        <Card key={i.id} className="rounded-xl hover:shadow-md transition">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                {i.category && <Badge variant="secondary" className="mb-2">{i.category}</Badge>}
                <h3 className="font-display font-semibold text-[hsl(var(--primary))]">{i.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{i.summary}</p>
              </div>
              {i.has_file && (
                <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700 bg-emerald-50 shrink-0">
                  <FileText className="w-3 h-3 mr-1" />
                  Word disponível
                </Badge>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setOpen(i)} data-testid={`idea-open-${i.id}`}>
                Ver detalhes
              </Button>
              {i.has_file && (
                <Button
                  size="sm"
                  onClick={() => downloadFile(i)}
                  disabled={downloading === i.id}
                  className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent)/0.9)]"
                  data-testid={`idea-download-${i.id}`}
                >
                  <Download className="w-4 h-4 mr-1" />
                  {downloading === i.id ? "Baixando…" : "Baixar arquivo"}
                </Button>
              )}
              {i.template_text && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    const ok = await copyToClipboard(i.template_text);
                    toast[ok ? "success" : "error"](ok ? "Modelo copiado!" : "Não foi possível copiar");
                  }}
                >
                  <Copy className="w-4 h-4 mr-1" /> Copiar texto
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-[560px] rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{open?.title}</DialogTitle></DialogHeader>
          {open && (
            <div className="space-y-3 text-sm">
              {open.category && <Badge variant="secondary">{open.category}</Badge>}
              {open.summary && (
                <div><p className="text-xs text-muted-foreground mb-1">Resumo</p><p>{open.summary}</p></div>
              )}
              {open.justification && (
                <div><p className="text-xs text-muted-foreground mb-1">Justificativa</p><p>{open.justification}</p></div>
              )}
              {open.technical_notes && (
                <div><p className="text-xs text-muted-foreground mb-1">Notas técnicas</p><p>{open.technical_notes}</p></div>
              )}
              {open.has_file && (
                <div className="pt-2">
                  <Button
                    onClick={() => downloadFile(open)}
                    disabled={downloading === open.id}
                    className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent)/0.9)]"
                    data-testid="idea-detail-download"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {downloading === open.id ? "Baixando…" : `Baixar ${open.file_name || "arquivo"}`}
                  </Button>
                </div>
              )}
              {open.template_text && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Texto modelo</p>
                  <pre className="whitespace-pre-wrap bg-[hsl(var(--secondary))] p-3 rounded-lg text-xs max-h-60 overflow-y-auto">{open.template_text}</pre>
                  <Button size="sm" className="mt-2" onClick={async () => {
                    const ok = await copyToClipboard(open.template_text);
                    toast[ok ? "success" : "error"](ok ? "Copiado" : "Não foi possível copiar");
                  }}>Copiar texto</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
